/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   pin bizo
 */

import { MediaType, PropertyFeatures } from '@zcode/enums'
import {
  mediaUrl,
  toBuildingShortUrl,
  toExternalBuildingUrl,
  toExternalListingUrl,
  toListingShortUrl,
} from '@zcode/nr-utils'
import type { Pin as PinType, RawPin } from '@zcode/state-type'
import { dollarize } from '@zcode/utils'
import { Bizo, autoclass } from './base'
import * as Media from './media'
import { getAgeText } from './rentable/utils/ageText'
import { bedsBathsRangeTxt } from './rentable/utils/bedsBathsRangeTxt'
import { getBedsBathsText } from './rentable/utils/getBedsBathsText'
import { getPricePerNight } from './rentable/utils/getPricePerNight'
import { isAgeRestricted } from './rentable/utils/isAgeRestricted'
import { isCustomMessageRequired } from './rentable/utils/isCustomMessageRequired'
import { isExclusive } from './rentable/utils/isExclusive'
import { isIncomeRestricted } from './rentable/utils/isIncomeRestricted'
import { isMilitaryHousingFeature } from './rentable/utils/isMilitaryHousingFeature'
import { isMmPrevent } from './rentable/utils/isMmPrevent'
import { isMoveInDateRequired } from './rentable/utils/isMoveInDateRequired'
import { isPhoneRequired } from './rentable/utils/isPhoneRequired'
import { isRentpath } from './rentable/utils/isRentpath'
import { isShortTermAvailability } from './rentable/utils/isShortTermAvailability'
import { isShortTermFeed } from './rentable/utils/isShortTermFeed'
import { isShortTermViewV1 } from './rentable/utils/isShortTermViewV1'
import { isStudentHousing } from './rentable/utils/isStudentHousing'
import { isThirdPartyPaid } from './rentable/utils/isThirdPartyPaid'
import { isTour } from './rentable/utils/isTour'
import { isTourDisabled } from './rentable/utils/isTourDisabled'
import { isUnavailable } from './rentable/utils/isUnavailable'
import { isVerified } from './rentable/utils/isVerified'
import { mightHaveInstantTourScheduling } from './rentable/utils/mightHaveInstantTourScheduling'
import * as RentableUtils from './rentable_utils'

export const PIN_DEFINITION = {
  address: 'address',
  buildingId: 'building_id',
  buildingName: 'building_name',
  count: (t: PinType) => t._data.floorplan_count ?? t._data.count,
  city: 'city',
  features: 'features',
  feedName: 'feed_name',
  floorplanCount: 'floorplan_count',
  imageIds: 'image_ids',
  isPad: 'is_pad',
  isMessageable: 'is_messageable',
  lat: 'lat',
  listingId: 'listing_id',
  listedOn: 'listed_on',
  lng: 'lng',
  minBathrooms: 'min_bathrooms',
  maxBathrooms: 'max_bathrooms',
  minBedrooms: 'min_bedrooms',
  maxBedrooms: 'max_bedrooms',
  minPrice: 'min_price',
  maxPrice: 'max_price',
  minSquareFeet: 'min_square_feet',
  maxSquareFeet: 'max_square_feet',
  neighborhoodName: 'neighborhood_name',
  paUrl: 'pa_url',
  pbId: 'pb_id',
  pbUrl: 'pb_url',
  plId: 'pl_id',
  plUrl: 'pl_url',
  promotionType: 'promotion_type',
  review: 'review',
  reviewsSummary: 'reviews_summary',
  supplementalDescription: 'supple_description',
  state: 'state',
  zipcode: 'zipcode',
  getAgeTextMini: (t: PinType): PinType['getAgeTextMini'] => getAgeText(t, true),
  combinedAmenities: () => [],
  isActive: (): PinType['isActive'] => true,
  isDirectDeal: (t: PinType) => !!t.features?.includes?.(PropertyFeatures.DIRECT_DEAL),
  isBoosted: (t: PinType) => !!t.features?.includes?.(PropertyFeatures.BOOSTED),
  isInstantApplyScreening: (t: PinType) =>
    !!t.features?.includes?.(PropertyFeatures.INSTANT_APPLY_SCREENING),
  isVerified,
  displayableAddress: (t: PinType) => t.address?.replace('undefined', '') ?? '',
  groupId: (t: PinType) => (t.buildingId ? -1 * t.buildingId : t.listingId),
  shortUrl: (t: PinType): PinType['shortUrl'] =>
    t.isBuilding && t.buildingId
      ? toBuildingShortUrl(t.buildingId)
      : t.listingId
        ? toListingShortUrl(t.listingId)
        : '', // last case here is likely unreachable
  externalUrl: (t: PinType): PinType['externalUrl'] =>
    t.isBuilding && t.buildingId
      ? toExternalBuildingUrl(t.buildingId)
      : t.listingId
        ? toExternalListingUrl(t.listingId)
        : '', // last case here is likely unreachable
  uId: (t: PinType): PinType['uId'] => {
    if (t.pbId) {
      return `b-p${t.pbId}` as const
    } else if (t.buildingId) {
      return `b-${t.buildingId}` as const
    } else if (t.plId) {
      return `l-${t.plId}p` as const
    } else {
      return `l-${t.listingId}` as const
    }
  },
  isBuilding: (t: PinType) => !!t.buildingId,
  isListing: (t: PinType) => !!(!t.buildingId && t.listingId),
  isMonetizedM3: (t: PinType) => t.isDirectDeal,
  isMonetizedM4: (t: PinType) => t.isDirectDeal || t.isThirdPartyPaid,
  url: 'url',
  headerText: RentableUtils.createListingMainHeader,
  mainImage: (t: PinType) =>
    Media.getImageUrlBySize({
      id: t.imageIds[0],
      noImage: true,
    }),
  images: (t: PinType): PinType['images'] => {
    return t.imageIds.map(
      (id) =>
        ({
          id,
          mediaType: MediaType.IMAGE,
          url: mediaUrl(id, Media.SIZE_XLARGE),
        }) as const
    )
  },
  hasImage: (t: PinType) => !!t.images?.length,
  priceText: RentableUtils.getPriceText,
  priceTxt: RentableUtils.getPriceTxt,
  minPriceText: (t: PinType) => dollarize(t.minPrice || 0),
  mightHaveInstantTourScheduling,
  floorplanText: (t: PinType): PinType['floorplanText'] => {
    if (t.count === 1) {
      return '1 Floor plan'
    }
    return `${t.count} Floor plans`
  },
  bathroomTxt: (t: PinType): PinType['bathroomTxt'] => {
    if (t.maxBathrooms === 1) {
      return '1 bath'
    }
    return `${t.maxBathrooms} baths`
  },
  bedroomTxt: (t: PinType): PinType['bedroomTxt'] => {
    switch (t.minBedrooms) {
      case -1:
        return 'Room'
      case 0:
        return 'Studio'
      case 1:
        return '1 Bed'
      default:
        return `${t.minBedrooms} Beds`
    }
  },
  bedroomAbbrTxtStr: (t: PinType) => {
    const bedrooms = t.minBedrooms
    if (bedrooms === -1) {
      return 'Room'
    }
    return bedrooms === 0 ? 'Studio' : `${bedrooms} br`
  },
  bedsBathsTxt: (t: PinType): PinType['bedsBathsTxt'] => getBedsBathsText(t, true),
  bedsRangeText: (t: PinType): PinType['bedsRangeText'] => {
    if (!t.buildingId) {
      return ''
    }
    if (t.maxBedrooms === -1) {
      return 'Rooms in'
    }

    const max =
      t.maxBedrooms === 0
        ? 'Studio'
        : t.maxBedrooms === 1
          ? '1 Bedroom'
          : (`${t.maxBedrooms} Bedrooms` as const)
    if (t.minBedrooms === -1) {
      return `Rooms - ${max}`
    }
    if (t.minBedrooms !== t.maxBedrooms) {
      const min = t.minBedrooms === 0 ? 'Studio' : t.minBedrooms
      return `${min} - ${max}`
    }

    return max
  },
  bedsBathsRangeTxt,
  bedroomText: (t: PinType): PinType['bedroomText'] => {
    switch (t.minBedrooms) {
      case -1:
        return 'Room'
      case 0:
        return 'Studio'
      case 1:
        return '1 Bedroom'
      default:
        return `${t.minBedrooms} Bedrooms`
    }
  },
  bathroomText: (t: PinType): PinType['bathroomText'] => {
    if (t.maxBathrooms === 0 || t.maxBathrooms === 1) {
      return `${t.maxBathrooms} Bath`
    }
    return `${t.maxBathrooms} Baths`
  },
  bedsBathsText: (t: PinType): PinType['bedsBathsText'] => {
    return `${t.bedroomText} · ${t.bathroomText}` as const
  },
  hoodCityText: (t: PinType): PinType['hoodCityText'] => {
    if (t.neighborhoodName && t.city) {
      return `${t.neighborhoodName}, ${t.city}` as const
    }
    return ''
  },
  isAgeRestricted,
  isCustomMessageRequired,
  isExclusive,
  isIncomeRestricted,
  isMilitaryHousingFeature,
  isMmPrevent,
  isMoveInDateRequired,
  isPhoneRequired,
  isRentpath,
  isShortTermAvailability,
  isShortTermFeed,
  isShortTermViewV1,
  isStudentHousing,
  isThirdPartyPaid,
  isTour,
  isTourDisabled,
  isUnavailable,
  pricePerNight: getPricePerNight,
  status: (t: PinType): PinType['status'] => t._data.listing_status ?? 1,
} as const

const Pin = autoclass<PinType, RawPin>(
  'Pin',
  class Pin extends Bizo<PinType, RawPin> {},
  PIN_DEFINITION,
  null
)

export default Pin
